import { makeAutoObservable } from 'mobx';
import React from 'react';
import { AccountInterface } from 'type/accountType';
import { accountApiService } from '../service/api/accountApiService';
import {
  ADMIN_MENU_ITEMS,
  CONTENT_MENU_ITEMS,
  CONTRACT_MENU_ITEMS,
  CP_MENU_KEY_LIST,
  INGEST_MENU_ITEMS,
  META_DATA_STRUCTURE_MENU_ITEMS,
  PERMISSION_MENU_ITEMS,
  REVENUE_MENU_ITEMS,
  SYNC_MENU_ITEMS,
  TOOLS_MENU_ITEMS,
  VENDOR_MENU_ITEMS,
  VIETNAM_BASE_MENU_KEY_LIST,
} from 'constants/menu';
import {
  VIETNAM_UPLOAD_INGEST_CODE,
  VIETNAM_UPLOAD_NEW_MENU,
  VIETNAM_UPLOAD_UPLOAD_CODE,
  VIETNAM_UPLOAD_VIEW_CODE,
  VIETNAM_UPLOADS_MENU,
} from 'constants/permission';

const SUPER_ADMIN_USER_ID = '1';

const collectMenuKeys = (items: { key?: React.Key }[] | undefined): string[] =>
  (items ?? [])
    .map(item => (typeof item?.key === 'string' ? item.key : ''))
    .filter((k): k is string => !!k);

const ALL_MENU_KEYS: string[] = [
  ...collectMenuKeys(CONTENT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(META_DATA_STRUCTURE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(CONTRACT_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(REVENUE_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(INGEST_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(SYNC_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(TOOLS_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(VENDOR_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(ADMIN_MENU_ITEMS as { key?: React.Key }[] | undefined),
  ...collectMenuKeys(PERMISSION_MENU_ITEMS as { key?: React.Key }[] | undefined),
];

class AccountStore {
  private account: AccountInterface | null = null;
  private projectId: number | null = null;
  private role: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public reset = (): void => {
    this.account = null;
    this.projectId = null;
    this.role = null;
  };

  public async init(): Promise<void> {
    const account = await accountApiService.fetchAccount();
    this.account = account;
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(this.account?.id)
    );
    this.projectId = projectId;
    this.role = role;
  }

  public async fetchAccount(): Promise<AccountInterface> {
    if (this.account) {
      return this.account;
    }
    const account = await accountApiService.fetchAccount();
    const { project_id: projectId, role } = await accountApiService.getUserProjectRoles(
      Number(account.id)
    );
    this.projectId = projectId;
    this.role = role;
    this.account = account;
    return account;
  }

  public async update(account: AccountInterface): Promise<void> {
    await accountApiService.update(Number(account.id), account);
  }

  public async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    await accountApiService.updatePassword(oldPassword, newPassword);
  }

  public setAccount = (account: AccountInterface): void => {
    this.account = account;
  };

  public setProject = (projectId: number): void => {
    if (this.projectId) {
      this.projectId = projectId;
    } else {
      throw new Error('Account not found');
    }
  };

  public getAccount = (): AccountInterface | null => {
    return this.account;
  };

  public getProject = (): number | null => {
    return this.projectId;
  };

  public getRole = (): string | null => {
    return this.role;
  };

  public isStaff = (): boolean => {
    return this.account?.is_staff ?? false;
  };

  public isInit = (): boolean => {
    return this.account !== null;
  };

  public isSuperAdmin = (): boolean => {
    return String(this.account?.id ?? '') === SUPER_ADMIN_USER_ID;
  };

  public getMenus = (): string[] => {
    // Super admin (user_id=1): 권한과 무관하게 모든 메뉴 노출.
    // 단, vendor-download는 벤더 사용자 전용 다운로드 페이지이므로 super admin에게도 숨김.
    if (this.isSuperAdmin()) {
      return ALL_MENU_KEYS.filter(k => k !== 'vendor-download');
    }

    const permissions = Array.from(this.account?.permissions ?? []);
    const permissionList = permissions.map(permission => permission.split(':')[0]);

    // Vendor-only 사용자(vendor-* 권한만 보유)는 INGEST/SYNC auto-push 제외하고
    // 본인의 vendor 메뉴만 노출.
    const isVendorOnly =
      permissionList.length > 0 && permissionList.every(key => key.startsWith('vendor-'));
    if (isVendorOnly) {
      return permissionList;
    }

    // LG Channel Vietnam 전용 계정: BE allowlist가 /api/v1/vietnam-upload/ 와 /api/v1/accounts/
    // 뿐이라 다른 도메인 메뉴는 클릭 즉시 403이다. 계정 계열 메뉴 + 보유 code에서 유도한 VN 메뉴만 남긴다.
    if (this.isLgChannelVietnam()) {
      return Array.from(new Set([...VIETNAM_BASE_MENU_KEY_LIST, ...this.getVietnamMenus()]));
    }

    permissionList.push(...INGEST_MENU_ITEMS!.map(item => item!.key as string));
    permissionList.push(...SYNC_MENU_ITEMS!.map(item => item!.key as string));

    const scoped =
      this.account?.platform.id === 2
        ? permissionList.filter(item => CP_MENU_KEY_LIST.includes(item))
        : permissionList;

    // VN 메뉴는 code(`vietnam_upload.*`)에서 유도한다 — 점 표기라 위 permissionList 분해로는
    // 메뉴 키가 나오지 않는다. CP 필터 뒤에 붙여 platform 2 계정에서도 유지된다.
    return Array.from(new Set([...scoped, ...this.getVietnamMenus()]));
  };

  public getPermissions = (): string[] => {
    return this.account?.permissions ?? [];
  };

  public hasPermission = (menu: string, action: string): boolean => {
    // Super admin은 모든 권한 통과.
    if (this.isSuperAdmin()) return true;
    const permissions = Array.from(this.account?.permissions ?? []);
    const permission = `${menu}:${action}`;
    return permissions.includes(permission);
  };

  /**
   * permission code 단위 확인. `vietnam_upload.*` 처럼 `{menu}:{action}`으로 분해되지 않는
   * code를 위한 것이다 — `hasPermission()`은 콜론 표기를 조립하므로 쓸 수 없다.
   */
  public hasPermissionCode = (code: string): boolean => {
    if (this.isSuperAdmin()) return true;
    return (this.account?.permissions ?? []).includes(code);
  };

  // --- LG Channel Vietnam 접근 규칙 (constants/permission.ts 참고) -----------------

  /**
   * LG Channel Vietnam 전용 계정 = user flag 또는 소속 company flag.
   * BE `common/scoping.is_lg_channel_vietnam()`과 같은 OR 판정이다.
   *
   * 이 계정은 BE `ScopedJWTAuthentication`이 `/api/v1/vietnam-upload/`·`/api/v1/accounts/`
   * 밖의 모든 API를 403으로 막으므로, 사이드바도 같은 범위로 축소한다.
   *
   * ODK 운영진에게는 flag를 부여하지 않는다 — BE `IsLgChannelVietnam`이 admin role을 flag 없이
   * 통과시키므로 필요가 없고, flag를 켜면 여기서 사이드바가 축소돼 버린다.
   */
  public isLgChannelVietnam = (): boolean => {
    if (this.isSuperAdmin()) return false;
    const account = this.account;
    if (!account) return false;
    return Boolean(account.is_lg_channel_vietnam || account.company?.is_lg_channel_vietnam);
  };

  /** 업로드 목록 조회 (`/vietnam-uploads`). 자기 company / 전 CP 범위는 서버가 정한다. */
  public canViewVietnamUploads = (): boolean => this.hasPermissionCode(VIETNAM_UPLOAD_VIEW_CODE);

  /** VN 업로드 화면 (`/assets/add-vn`). */
  public canAddVietnamUpload = (): boolean => this.hasPermissionCode(VIETNAM_UPLOAD_UPLOAD_CODE);

  /**
   * Ingest 실행. code 보유가 필요조건이고, 대상이 자기 company 레코드여야 한다 —
   * BE는 admin이라도 타 company 레코드에 404를 준다(쓰기 경로에는 우회가 없다).
   * 그래서 호출측에서 `record.company_slug === account.company.slug`를 함께 확인한다.
   */
  public canIngestVietnamUpload = (): boolean => this.hasPermissionCode(VIETNAM_UPLOAD_INGEST_CODE);

  /** 보유 code에서 유도한 VN 사이드바 메뉴 키. */
  public getVietnamMenus = (): string[] => {
    const menus: string[] = [];
    if (this.canViewVietnamUploads()) menus.push(VIETNAM_UPLOADS_MENU);
    if (this.canAddVietnamUpload()) menus.push(VIETNAM_UPLOAD_NEW_MENU);
    return menus;
  };
}

export const accountStore = new AccountStore();
